import { useMemo, useState, type ReactNode } from "react";
import { Alert, Button, ConfirmModal, useToast } from "@a2z/ui";
import type { StoreThemeJson, TemplateName } from "@a2z/storefront-schemas";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ThemeValidationErrorItem, ThemeVersion } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useStoresQuery } from "@/modules/stores/services/queries/useStoresQuery";
import { useUpdateThemeVersionJsonMutation } from "../services/useUpdateThemeVersionJsonMutation";
import { usePublishThemeVersionMutation } from "../services/usePublishThemeVersionMutation";
import { SectionList } from "./components/SectionList";
import { SectionInspector } from "./components/SectionInspector";
import { StudioPreview, type PreviewDevice } from "./components/StudioPreview";
import { RawJsonPanel } from "./components/RawJsonPanel";
import { BrandMotionPanel } from "./components/BrandMotionPanel";
import { ThumbnailField } from "./components/ThumbnailField";
import { useThemeDraft } from "./state/useThemeDraft";

/**
 * The visual catalog-theme editor. Replaces the raw-JSON textarea that used to
 * be the whole of `ThemeVersionEditor` (which is still reachable, folded into
 * the disclosure at the bottom — see `RawJsonPanel` for why removing it would
 * be a regression).
 *
 * Three panes: section list │ live storefront preview │ inspector.
 *
 * Design notes worth reading before changing this:
 *  - Preview strategy and why the seller's token mechanism does not transfer:
 *    `docs/plans/2026-07-20-admin-theme-studio.md` §1.
 *  - What is shared with the seller editor vs admin-only, and the extraction
 *    plan that removes the remaining duplication: §2 of the same doc.
 */

const TEMPLATES: TemplateName[] = ["home", "product", "collection", "cart"];
const DEVICES: PreviewDevice[] = ["desktop", "tablet", "mobile"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function extractValidationErrors(payload: unknown): ThemeValidationErrorItem[] {
  if (!isRecord(payload) || !Array.isArray(payload.errors)) return [];
  return payload.errors.filter(
    (item): item is ThemeValidationErrorItem =>
      isRecord(item) &&
      typeof item.path === "string" &&
      typeof item.code === "string" &&
      typeof item.message === "string",
  );
}

export function ThemeStudio({
  themeId,
  version,
  locale,
  detailsPanel,
  readOnly = false,
  onCreateVersion,
  isCreatingVersion = false,
}: {
  themeId: string;
  version: ThemeVersion;
  locale: "ar" | "en";
  /**
   * View a PUBLISHED version. A published version is immutable server-side
   * (theme-catalog.service refuses json edits), so the studio still shows the
   * section list + preview + inspector — you can SEE every section — but Save/
   * Publish are replaced by a "create a new version to edit" affordance, and the
   * list/inspector panes are non-interactive so nothing edited here is silently
   * lost. This is what makes selecting a published-only theme show its sections
   * instead of just the metadata form.
   */
  readOnly?: boolean;
  /** Called by the read-only banner's edit button (creates a draft version). */
  onCreateVersion?: () => void;
  isCreatingVersion?: boolean;
  /**
   * The theme's metadata form, revealed by a toolbar toggle rather than
   * occupying the top of the page permanently.
   *
   * Passed in rather than imported so the studio does not depend on
   * `ThemeEditForm`: the page owns the theme record and its mutations, the
   * studio owns the toolbar and the layout. That keeps the studio usable
   * anywhere a theme version is edited, with or without a metadata form.
   */
  detailsPanel?: ReactNode;
}) {
  const t = useTranslations();
  const toast = useToast();

  const initial = version.json as unknown as StoreThemeJson;
  const draft = useThemeDraft(initial);

  const [device, setDevice] = useState<PreviewDevice>("desktop");
  // Closed by default: the metadata is set once when the theme is created and
  // rarely touched after, whereas the three panes are the working surface.
  const [showDetails, setShowDetails] = useState(false);
  const [storeSlug, setStoreSlug] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    ThemeValidationErrorItem[]
  >([]);
  const [isPublishOpen, setIsPublishOpen] = useState(false);

  const { mutateAsync: saveJson, isPending: isSaving } =
    useUpdateThemeVersionJsonMutation();
  const { mutateAsync: publish, isPending: isPublishing } =
    usePublishThemeVersionMutation();

  // Render-target candidates. Reference stores first — they are the ones seeded
  // with real depth (categories AND products AND imagery), which is what a
  // theme's `$category:N` / `$products:N` slots need to resolve. A thin store
  // silently drops sections and makes the theme look broken.
  const storesQuery = useStoresQuery({ limit: 100 });
  const storeOptions = useMemo(() => {
    const stores = storesQuery.data?.items ?? [];
    return [...stores].sort((a, b) => {
      const aRef = a.storeSlug.startsWith("demo-") ? 0 : 1;
      const bRef = b.storeSlug.startsWith("demo-") ? 0 : 1;
      if (aRef !== bRef) return aRef - bRef;
      return a.storeSlug.localeCompare(b.storeSlug);
    });
  }, [storesQuery.data]);

  const effectiveStoreSlug = storeSlug ?? storeOptions[0]?.storeSlug ?? null;

  const handleSave = async () => {
    setSaveError(null);
    setValidationErrors([]);
    try {
      const updated = await saveJson({
        themeId,
        versionId: version.id,
        schemaVersion: draft.draft.schemaVersion,
        settings: draft.draft.settings as unknown as Record<string, unknown>,
        templates: draft.draft.templates as unknown as Record<string, unknown>,
        pages: draft.draft.pages as unknown as Record<string, unknown>,
      });
      // Re-baseline from the SERVER's copy, not the local draft: the backend
      // may normalise on write, and baselining from the local object would
      // leave `isDirty` false while the two actually differ.
      draft.markSaved(updated.json as unknown as StoreThemeJson);
      // The preview reads the persisted row, so it is only now that a reload
      // shows the new state.
      setReloadKey((k) => k + 1);
      toast.success(t(keys.themes.saveSuccess));
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setSaveError(error.message || t(keys.errors.generic));
        setValidationErrors(extractValidationErrors(error.payload));
      } else {
        setSaveError(t(keys.errors.generic));
      }
    }
  };

  const handlePublish = async () => {
    setPublishError(null);
    setValidationErrors([]);
    try {
      await publish({ themeId, versionId: version.id });
      setIsPublishOpen(false);
      toast.success(t(keys.themes.publishSuccess));
    } catch (error) {
      setIsPublishOpen(false);
      if (error instanceof ApiJsonClientError) {
        setPublishError(error.message || t(keys.errors.generic));
        setValidationErrors(extractValidationErrors(error.payload));
      } else {
        setPublishError(t(keys.errors.generic));
      }
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {saveError || publishError ? (
        <Alert
          message={publishError ?? saveError ?? ""}
          helperText={t(keys.errors.recovery)}
        />
      ) : null}

      {validationErrors.length > 0 ? (
        <div className="rounded-xl border border-[color:var(--error-500)] bg-[color:var(--error-100)] px-4 py-3">
          <div className="text-[12px] font-semibold text-[color:var(--error-600)]">
            {t(keys.themes.publishValidationTitle)}
          </div>
          <ul className="mt-2 flex flex-col gap-1">
            {validationErrors.map((item, index) => (
              <li
                key={`${item.path}-${item.code}-${index}`}
                className="text-[11.5px] text-[color:var(--error-600)]"
              >
                <span className="font-mono font-semibold">{item.path}</span>
                {" — "}
                {item.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 py-2">
        <select
          aria-label={t(keys.themes.studioTemplate)}
          className="rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-2 py-1.5 text-[12px]"
          value={draft.activeTemplate}
          onChange={(e) =>
            draft.setActiveTemplate(e.target.value as TemplateName)
          }
        >
          {TEMPLATES.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        <select
          aria-label={t(keys.themes.studioPreviewStore)}
          className="rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-2 py-1.5 text-[12px]"
          value={effectiveStoreSlug ?? ""}
          onChange={(e) => setStoreSlug(e.target.value)}
        >
          {storeOptions.map((store) => (
            <option key={store.storeSlug} value={store.storeSlug}>
              {store.storeSlug}
            </option>
          ))}
        </select>

        <div className="flex gap-1">
          {DEVICES.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setDevice(name)}
              className={`rounded-lg border px-2.5 py-1.5 text-[11.5px] font-semibold capitalize ${
                device === name
                  ? "border-[color:var(--primary-500)] bg-[color:var(--primary-100)] text-[color:var(--primary-700)]"
                  : "border-[color:var(--border-light)] text-[color:var(--text-primary)]"
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        {/* Same visual language as the device switcher — it is the same kind
            of control: a view toggle, not an action. `aria-pressed` rather
            than a plain button so assistive tech reports the open/closed state
            the highlight conveys visually. Reuses the metadata title as its
            label, so no new string ships. */}
        {detailsPanel ? (
          <button
            type="button"
            aria-pressed={showDetails}
            aria-controls="theme-details-panel"
            onClick={() => setShowDetails((open) => !open)}
            className={`rounded-lg border px-2.5 py-1.5 text-[11.5px] font-semibold ${
              showDetails
                ? "border-[color:var(--primary-500)] bg-[color:var(--primary-100)] text-[color:var(--primary-700)]"
                : "border-[color:var(--border-light)] text-[color:var(--text-primary)]"
            }`}
          >
            {t(keys.themes.metadataTitle)}
          </button>
        ) : null}

        <div className="ms-auto flex items-center gap-2">
          {readOnly ? (
            // Published version: Save/Publish don't apply (it's immutable). Offer
            // the one action that does — branch a new editable draft.
            onCreateVersion ? (
              <Button
                type="button"
                variant="primary"
                disabled={isCreatingVersion}
                onClick={onCreateVersion}
                className="h-9 rounded-full px-4 text-xs font-semibold"
              >
                {t(keys.themes.newVersion)}
              </Button>
            ) : null
          ) : (
            <>
              {draft.isDirty ? (
                <span className="text-[11px] font-semibold text-[color:var(--warning-700)]">
                  {t(keys.themes.studioUnsaved)}
                </span>
              ) : null}
              <Button
                type="button"
                variant="primary"
                disabled={isSaving || !draft.isDirty}
                onClick={handleSave}
                className="h-9 rounded-full px-4 text-xs font-semibold"
              >
                {t(keys.themes.studioSave)}
              </Button>
              <Button
                type="button"
                variant="outline"
                // Publish freezes the last-SAVED server json, not the editor
                // state. Blocking on dirty is what stops unsaved edits from being
                // silently discarded into an immutable version.
                disabled={isPublishing || draft.isDirty}
                onClick={() => {
                  setPublishError(null);
                  setValidationErrors([]);
                  setIsPublishOpen(true);
                }}
                className="h-9 rounded-full px-4 text-xs font-semibold"
              >
                {t(keys.themes.publish)}
              </Button>
            </>
          )}
        </div>
      </div>

      {readOnly ? (
        <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-2.5 text-[12px] text-[color:var(--text-muted)]">
          {t(keys.themes.studioReadOnlyNote)}
        </div>
      ) : null}

      {/* Metadata, revealed by the toolbar toggle. Kept at reading width — a
          form does not get easier to read by being stretched across a
          full-bleed page. Unmounted when closed rather than hidden, so a
          half-typed description cannot be silently submitted from a panel the
          admin cannot see. */}
      {detailsPanel && showDetails ? (
        <div
          id="theme-details-panel"
          className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-4"
        >
          <div className="w-full max-w-3xl">{detailsPanel}</div>
        </div>
      ) : null}

      {/* Three panes. In read-only, the list + inspector are non-interactive
          (a published version can't be edited here) so the section list and
          inspector are for VIEWING; the preview stays fully interactive. */}
      <div className="grid h-[70vh] min-h-[520px] grid-cols-1 gap-3 lg:grid-cols-[minmax(200px,15rem)_1fr_minmax(240px,19rem)]">
        <div
          className={`overflow-hidden rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)]${
            readOnly ? " pointer-events-none opacity-90" : ""
          }`}
          aria-disabled={readOnly || undefined}
        >
          <SectionList draft={draft} lang={locale} />
        </div>

        <div className="overflow-hidden rounded-xl border border-[color:var(--border-light)]">
          <StudioPreview
            themeId={themeId}
            versionId={version.id}
            storeSlug={effectiveStoreSlug}
            device={device}
            locale={locale}
            reloadKey={reloadKey}
          />
        </div>

        <div
          className={`overflow-hidden rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)]${
            readOnly ? " pointer-events-none opacity-90" : ""
          }`}
          aria-disabled={readOnly || undefined}
        >
          <SectionInspector draft={draft} lang={locale} />
        </div>
      </div>

      <p className="text-[11px] text-[color:var(--text-muted)]">
        {t(keys.themes.studioPreviewNote)}
      </p>

      {/* Thumbnail is version METADATA, not part of the rendered payload, so the
          backend allows writing it even on a published (frozen) version (see
          updateVersionMeta). It therefore renders regardless of readOnly —
          otherwise a published theme's gallery image could never be set/changed
          without cutting a whole new version. */}
      <ThumbnailField themeId={themeId} version={version} />

      {/* Editors below the panes need a mutable draft — hidden when viewing a
          published version (the section list + preview above already show what
          the theme contains). Create a new version to reach them. */}
      {readOnly ? null : (
        <>
          {/* Theme-level MOTION control (scroll-reveal driver). Brand-wide, so
              it lives outside the per-section inspector — see BrandMotionPanel. */}
          <BrandMotionPanel draft={draft} lang={locale} />

          <RawJsonPanel draft={draft} />
        </>
      )}

      <ConfirmModal
        isOpen={isPublishOpen}
        onClose={() => setIsPublishOpen(false)}
        onConfirm={handlePublish}
        tone="primary"
        title={t(keys.themes.publishConfirmTitle)}
        subtitle={t(keys.themes.publishConfirmSubtitle)}
        description={t(keys.themes.publishConfirmBody)}
        confirmLabel={t(keys.themes.publish)}
        cancelLabel={t(keys.common.cancel)}
        closeLabel={t(keys.common.close)}
        isSubmitting={isPublishing}
      />
    </div>
  );
}
